import React, { useEffect, useState, useRef } from 'react';
import { TreeGridComponent, ColumnsDirective, ColumnDirective, Inject, Edit } from '@syncfusion/ej2-react-treegrid';
import * as Inovx from '@shared';
import { RowDataBoundEventArgs, RowSelectingEventArgs } from '@syncfusion/ej2-react-grids';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import FacilityStore from '../../stores/FacilityStore';
import ComponentHelpers from '@facility/helpers/ComponentHelpers';
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;

interface TreeNode {
    ID: string;
    Name: string;
    ParentID?: string | null;
    ClassificationTags?: TreeNode[];
    isExpanded?: boolean;
    PermissionLevel?: 0 | 1 | 2 | 3;
    VersionToken?: string | null
}
function buildTree(data: IClassificationTag[], expandedIds: string[]): TreeNode[] {
    const map = new Map<string, TreeNode>();
    const roots: TreeNode[] = [];

    const sorted = [...data].sort((a, b) =>
        a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' })
    );
    // Step 1: clone nodes and initialize children
    sorted.forEach(item => {
        map.set(item.ID, {
            ...item,
            ClassificationTags: [],
            isExpanded: expandedIds.includes(item.ID),
        });
    });

    // Step 2: assign children to parents
    sorted.forEach(item => {
        const node = map.get(item.ID)!;

        if (item?.ParentID) {
            const parent = map.get(item.ParentID);
            if (parent) {
                parent.ClassificationTags!.push(node);
            }
        } else {
            roots.push(node);
        }
    });

    return roots;
}

/* ------------------ COMPONENT ------------------ */
interface TagProps {
    selectedTags?: string | string[];
    onSelectedTagsChange?: (selectedIds: string[]) => void;
    taxonomyType?: TaxonomyType;
    taxonomyClassificationDetailOverride?: ILibraryTaxonomyDetail | null;
    restrictToCurrentUserPermissions?: boolean;
    defaultSelectEnabledTags?: boolean;
    allowViewOnlyTagSelection?: boolean;
    allowParentChildSelection?: boolean;
    height?: string | number;
    showSelectAll?: boolean;
}

const ClassificationTags: React.FC<TagProps> = ({ selectedTags, onSelectedTagsChange, taxonomyType = TaxonomyType.KB, taxonomyClassificationDetailOverride, restrictToCurrentUserPermissions = false, defaultSelectEnabledTags = false, allowViewOnlyTagSelection = false, allowParentChildSelection = false, height, showSelectAll = false }) => {
    const treeGridRef = useRef<TreeGridComponent>(null);

    const [taxonomyClassificationDetail, setTaxonomyClassificationDetail] = useState<ILibraryTaxonomyDetail>(
        restrictToCurrentUserPermissions
            ? taxonomyClassificationDetailOverride ?? FacilityStore.getVisibleTaxonomyClassificationDetail(taxonomyType)
            : taxonomyClassificationDetailOverride ?? FacilityStore.getTaxonomyClassificationDetail(taxonomyType)
    );
    const [expandedRows, setExpandedRows] = useState<string[]>([]);
    const selectedTagIdsRef = useRef<string[]>([]);
    const hasInitializedDefaultSelectionRef = useRef(false);

    const loadState = () => {
        if (taxonomyClassificationDetailOverride !== undefined) {
            setTaxonomyClassificationDetail(taxonomyClassificationDetailOverride);
            return;
        }

        setTaxonomyClassificationDetail(
            restrictToCurrentUserPermissions
                ? FacilityStore.getVisibleTaxonomyClassificationDetail(taxonomyType)
                : FacilityStore.getTaxonomyClassificationDetail(taxonomyType)
        )
    };

    useEffect(() => {
        if (taxonomyClassificationDetailOverride !== undefined) {
            setTaxonomyClassificationDetail(taxonomyClassificationDetailOverride);
        }
    }, [taxonomyClassificationDetailOverride]);
    
    useEffect(() => {
        FacilityStore.addChangeListener(loadState);
        return () => {
            FacilityStore.removeChangeListener(loadState);
        };
    }, [restrictToCurrentUserPermissions, taxonomyClassificationDetailOverride, taxonomyType]);

    const selectedTagIds = React.useMemo<string[]>(() => {
        if (!selectedTags) return [];
        return Array.isArray(selectedTags) ? selectedTags : [selectedTags];
    }, [selectedTags]);

    useEffect(() => {
        selectedTagIdsRef.current = selectedTagIds;
    }, [selectedTagIds]);

    const classificationTags = taxonomyClassificationDetail?.ClassificationTags ?? [];

    const hierarchyParentMap = React.useMemo(() => {
        const map = new Map<string, string | null>();
        classificationTags.forEach(tag => {
            map.set(tag.ID, tag.ParentID ?? null);
        });
        return map;
    }, [classificationTags]);

    const getPermissionLevelForTag = React.useCallback((tag?: IClassificationTag): 0 | 1 | 2 | 3 => {
        return ComponentHelpers.getClassificationTagPermissionLevel(tag, classificationTags);
    }, [classificationTags]);

    const visibleClassificationTags = React.useMemo<IClassificationTag[]>(() => {
        if (!restrictToCurrentUserPermissions) {
            return classificationTags;
        }

        const allowedTagIds = new Set<string>();

        classificationTags.forEach(tag => {
            if (getPermissionLevelForTag(tag) >= 1) {
                allowedTagIds.add(tag.ID);
            }
        });

        if (allowedTagIds.size === 0) {
            return [];
        }

        const visibleTagIds = new Set<string>();

        allowedTagIds.forEach(tagId => {
            let currentTagId: string | null = tagId;

            while (currentTagId) {
                if (visibleTagIds.has(currentTagId)) {
                    break;
                }

                visibleTagIds.add(currentTagId);
                currentTagId = hierarchyParentMap.get(currentTagId) ?? null;
            }
        });

        return classificationTags.filter(tag => visibleTagIds.has(tag.ID));
    }, [classificationTags, getPermissionLevelForTag, hierarchyParentMap, restrictToCurrentUserPermissions]);

    const visibleClassificationTagsWithPermissions = React.useMemo(() => {
        return visibleClassificationTags.map(tag => ({
            ...tag,
            PermissionLevel: getPermissionLevelForTag(tag)
        }));
    }, [getPermissionLevelForTag, visibleClassificationTags]);

    const enabledClassificationTagIds = React.useMemo(() => {
        const enabledTagIds = new Set(
            visibleClassificationTagsWithPermissions
                .filter(tag => !(restrictToCurrentUserPermissions && tag.PermissionLevel === 1))
                .map(tag => tag.ID)
        );

        return visibleClassificationTagsWithPermissions
            .filter(tag => !(restrictToCurrentUserPermissions && tag.PermissionLevel === 1))
            .filter(tag => {
                let parentId = hierarchyParentMap.get(tag.ID) ?? null;
                while (parentId) {
                    if (enabledTagIds.has(parentId)) {
                        return false;
                    }

                    parentId = hierarchyParentMap.get(parentId) ?? null;
                }

                return true;
            })
            .map(tag => tag.ID);
    }, [hierarchyParentMap, restrictToCurrentUserPermissions, visibleClassificationTagsWithPermissions]);

    useEffect(() => {
        if (
            !defaultSelectEnabledTags ||
            hasInitializedDefaultSelectionRef.current ||
            enabledClassificationTagIds.length === 0
        ) {
            return;
        }

        hasInitializedDefaultSelectionRef.current = true;

        if (selectedTagIds.length === 0) {
            onSelectedTagsChange?.(enabledClassificationTagIds);
        }
    }, [defaultSelectEnabledTags, enabledClassificationTagIds, onSelectedTagsChange, selectedTagIds.length]);

    const treeDataSource = React.useMemo<TreeNode[]>(() => {
        if (!visibleClassificationTagsWithPermissions.length) return [];
        const tree = buildTree(visibleClassificationTagsWithPermissions, expandedRows);
        return tree;
    }, [expandedRows, visibleClassificationTagsWithPermissions]);

    function collectParentIds(
        selectedIds: string[],
        allTags: IClassificationTag[]
    ): string[] {
        const parentMap = new Map<string, string | null>();

        allTags.forEach(tag => {
            parentMap.set(tag.ID, tag.ParentID ?? null);
        });

        const expanded = new Set<string>();

        selectedIds.forEach(id => {
            let currentParent = parentMap.get(id);

            while (currentParent) {
                expanded.add(currentParent);
                currentParent = parentMap.get(currentParent) ?? null;
            }
        });

        return Array.from(expanded);
    }

    useEffect(() => {
        if (
            !taxonomyClassificationDetail?.ClassificationTags ||
            selectedTagIds.length === 0
        ) {
            return;
        }

        const parentIds = collectParentIds(
            selectedTagIds,
            taxonomyClassificationDetail.ClassificationTags
        );

        setExpandedRows(prev => {
            const merged = new Set([...prev, ...parentIds]);
            return Array.from(merged);
        });
    }, [selectedTagIds, taxonomyClassificationDetail]);

    const expandRowsInGrid = (ids: string[]) => {
        const grid = treeGridRef.current as any;
        if (!grid) return;

        const records = grid.getCurrentViewRecords() as any[];

        ids.forEach(id => {
            const rowIndex = records.findIndex(r => r.ID === id);
            if (rowIndex >= 0) {
                const row = grid.getRowByIndex(rowIndex);
                if (row && !row.classList.contains('e-treegridcollapse')) {
                    grid?.expandRow(row);
                }
            }
        });
    };

    const buildRelationMaps = (tags: IClassificationTag[]) => {
        const parentMap = new Map<string, string | null>();
        const childrenMap = new Map<string, string[]>();

        tags.forEach(tag => {
            parentMap.set(tag.ID, tag.ParentID ?? null);

            if (tag.ParentID) {
                if (!childrenMap.has(tag.ParentID)) {
                    childrenMap.set(tag.ParentID, []);
                }
                childrenMap.get(tag.ParentID)!.push(tag.ID);
            }
        });

        return { parentMap, childrenMap };
    };

    const getAncestors = (id: string, parentMap: Map<string, string | null>) => {
        const ancestors: string[] = [];
        let current = parentMap.get(id);

        while (current) {
            ancestors.push(current);
            current = parentMap.get(current) ?? null;
        }
        return ancestors;
    };

    const getDescendants = (
        id: string,
        childrenMap: Map<string, string[]>
    ) => {
        const result: string[] = [];

        const dfs = (current: string) => {
            const children = childrenMap.get(current) ?? [];
            for (const child of children) {
                result.push(child);
                dfs(child);
            }
        };

        dfs(id);
        return result;
    };

    const { parentMap, childrenMap } = React.useMemo(() => {
        if (!taxonomyClassificationDetail?.ClassificationTags) {
            return {
                parentMap: new Map<string, string | null>(),
                childrenMap: new Map<string, string[]>()
            };
        }
        return buildRelationMaps(taxonomyClassificationDetail.ClassificationTags);
    }, [taxonomyClassificationDetail]);

    const syncCheckboxes = () => {
        const grid = treeGridRef.current as any;
        if (!grid) return;

        const selectAll = grid.element.querySelector('.classification-select-all') as HTMLInputElement | null;
        if (selectAll) {
            const allSelected = enabledClassificationTagIds.length > 0 && enabledClassificationTagIds.every(id => selectedTagIdsRef.current.includes(id));
            selectAll.checked = allSelected;
            selectAll.indeterminate = selectedTagIdsRef.current.length > 0 && !allSelected;
        }

        const rows = grid.getRows() as HTMLElement[];
        const records = grid.getCurrentViewRecords() as TreeNode[];

        if (!rows || !records) return;
        rows.forEach((row, index) => {
            const checkbox = row.querySelector('.row-checkbox') as HTMLInputElement | null;
            if (!checkbox) return;

            const data = records[index];
            if (!data) return;

            checkbox.checked = selectedTagIdsRef.current.includes(data.ID);
        });
    };

    useEffect(() => {
        syncCheckboxes();
    }, [selectedTagIds]);

    const handleDataBound = () => {
        const grid = treeGridRef.current as any;
        if (!grid) return;

        if (expandedRows.length > 0) {
            expandRowsInGrid(expandedRows);
        }
        syncCheckboxes();
    };

    const handleQueryCellInfo = (args: any) => {

        if (args.column.field !== 'Name') return;

        const item = args.data as TreeNode;
        const cell = args.cell as HTMLElement;
        const treeCell = cell.querySelector('.e-treecell') as HTMLElement;
        if (!treeCell) return;

        // Avoid duplicate render
        if (treeCell.querySelector('.facility-hierarchy-treegrid-name')) return;

        const text = treeCell.textContent?.trim() ?? '';
        let labelClass = 'facility-hierarchy-treegrid-name-label'
        if (!args?.data?.ParentID) labelClass += ' parent-name';
        treeCell.innerHTML = `<div class="facility-hierarchy-treegrid-name">
            <div class="checkbox-container">
                <input type="checkbox" class="row-checkbox" />
            </div>
            <div class="facility-hierarchy-treegrid-name-img">
                <img class="e-treeoverviewimage" src="${UrlUtilities.getBaseApplicationUrl()}/images/icons/tag.png" />
            </div>
            <div class="${labelClass}">
                ${text}
            </div>
        </div>`;

        const checkbox = treeCell.querySelector('.row-checkbox') as HTMLInputElement;
        checkbox.checked = selectedTagIds.includes(item.ID);
        checkbox.disabled = restrictToCurrentUserPermissions && !allowViewOnlyTagSelection && item.PermissionLevel === 1;
        checkbox.onclick = (e) => {
            e.stopPropagation();

            const current = selectedTagIdsRef.current;
            let updated = [...current];

            if (checkbox.checked) {
                if (!allowParentChildSelection) {
                    const ancestors = getAncestors(item.ID, parentMap);
                    const descendants = getDescendants(item.ID, childrenMap);

                    // Remove ancestors & descendants
                    updated = updated.filter(
                        id => !ancestors.includes(id) && !descendants.includes(id)
                    );
                }

                // Add current
                if (!updated.includes(item.ID)) {
                    updated.push(item.ID);
                }
            } else {
                // Simple remove
                updated = updated.filter(id => id !== item.ID);
            }

            onSelectedTagsChange?.(updated);
        };

    };


    const updateExpandedStatus = (args: any) => {
        const id = args?.data?.ID;
        if (!id) return;

        setExpandedRows(prev => {
            if (args.name === 'expanded') {
                return prev.includes(id) ? prev : [...prev, id];
            }
            if (args.name === 'collapsed') {
                return prev.filter(x => x !== id);
            }
            return prev;
        });
    };
    const editSettings = {
        allowEditing: true,
        mode: "Cell",
    };
    return (
        <div className="kl-taxonomy">
            <div className='facility-hierarchy-form'>
                <div className="facility-taxonomy-hierarchy-left-panel">
                    <TreeGridComponent
                        ref={treeGridRef}
                        dataSource={treeDataSource}
                        allowSelection={false}
                        editSettings={editSettings}
                        expandStateMapping="isExpanded"
                        expanded={updateExpandedStatus}
                        collapsed={updateExpandedStatus}
                        dataBound={handleDataBound}
                        idMapping="ID"
                        parentIdMapping="ParentID"
                        childMapping="ClassificationTags"
                        treeColumnIndex={0}
                        height={height ?? "100%"}
                        rowHeight={36}
                        queryCellInfo={handleQueryCellInfo}
                    >
                        <ColumnsDirective>
                            <ColumnDirective field="Name" headerText={InovxLoader.t('facility:header.classification')} allowEditing={false} editType="stringedit"
                                headerTemplate={showSelectAll ? () => (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
                                        <input
                                            className="classification-select-all"
                                            type="checkbox"
                                            aria-label={InovxLoader.t('facility:knowledgeBook.selectAll')}
                                            disabled={enabledClassificationTagIds.length === 0}
                                            ref={element => {
                                                if (element) {
                                                    element.checked = enabledClassificationTagIds.length > 0 && enabledClassificationTagIds.every(id => selectedTagIdsRef.current.includes(id));
                                                    element.indeterminate = selectedTagIdsRef.current.length > 0 && !element.checked;
                                                }
                                            }}
                                            onChange={event => onSelectedTagsChange?.(event.target.checked ? enabledClassificationTagIds : [])}
                                        />
                                        {InovxLoader.t('facility:header.classification')}
                                    </label>
                                ) : undefined}
                            />

                            <ColumnDirective field="ID" visible={false} isPrimaryKey />

                        </ColumnsDirective>
                        <Inject services={[Edit]} />

                    </TreeGridComponent>
                </div>
            </div>

        </div>
    );
};

export default ClassificationTags;
